import React, { useCallback, DragEvent } from 'react';
import { cn } from '../../utils/cn';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { FolderNode } from './FolderNode';

export const FolderTree: React.FC = () => {
  const {
    folders,
    rootFolderId,
    selectedFolderId,
    selectFolder,
    dropTargetId,
    externalDropTargetId,
    setExternalDropTarget,
    importExternalFiles,
  } = useProjectPanelStore();
  const rootFolder = folders[rootFolderId];

  // External file drag handlers
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    // Only handle external file drops
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      // Set the root folder as drop target when hovering over the general area
      if (!externalDropTargetId) {
        setExternalDropTarget(selectedFolderId || rootFolderId);
      }
    }
  }, [externalDropTargetId, selectedFolderId, rootFolderId, setExternalDropTarget]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    // Only clear if leaving the container entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setExternalDropTarget(null);
    }
  }, [setExternalDropTarget]);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const targetFolderId = externalDropTargetId || selectedFolderId || rootFolderId;
      importExternalFiles(files, targetFolderId);
    }

    setExternalDropTarget(null);
  }, [externalDropTargetId, selectedFolderId, rootFolderId, importExternalFiles, setExternalDropTarget]);

  if (!rootFolder) return null;

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-zinc-900 border-r border-zinc-700 transition-colors',
        externalDropTargetId && 'bg-blue-900/20'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="px-2 py-1.5 text-xs font-medium text-zinc-400 uppercase tracking-wide border-b border-zinc-700">
        Folders
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        <FolderNode
          folder={rootFolder}
          level={0}
          selectedId={selectedFolderId}
          onSelect={selectFolder}
          dropTargetId={dropTargetId}
          externalDropTargetId={externalDropTargetId}
        />
      </div>
    </div>
  );
};
